import React, {useEffect, useState} from 'react';
import {Card, Table, Badge, Dropdown, Pagination, Form, Button, Row, Col} from 'react-bootstrap';
import {filterReservationHistory} from '../../../services/OrderService';

const svg1 = (
    <svg width='20px' height='20px' viewBox='0 0 24 24' version='1.1'>
        <g stroke='none' strokeWidth='1' fill='none' fillRule='evenodd'>
            <rect x='0' y='0' width='24' height='24'></rect>
            <circle fill='#000000' cx='5' cy='12' r='2'></circle>
            <circle fill='#000000' cx='12' cy='12' r='2'></circle>
            <circle fill='#000000' cx='19' cy='12' r='2'></circle>
        </g>
    </svg>
);

const ReservationList = props => {
    const {
        reservationList, 
        getReservations, 
        pagination, 
        pageItems = 10, 
        onPageChange, 
        onPageItemsChange,
        onDateFilter
    } = props;
    const [selectedFilter, setSelectedFilter] = useState('All Status');
    const [filteredList, setFilteredList] = useState([]);
    const [currentPageItems, setCurrentPageItems] = useState(pageItems);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    useEffect(() => {
        getReservations(1, currentPageItems);
    }, [getReservations, currentPageItems]);
    
    // Auto-close toast after 3 seconds
    useEffect(() => {
        if (showToast) {
            const timer = setTimeout(() => {
                setShowToast(false);
            }, 3000);
            
            return () => clearTimeout(timer);
        }
    }, [showToast]);

    useEffect(() => {
        if (reservationList) {
            if (selectedFilter === 'All Status') {
                setFilteredList(reservationList);
            } else {
                handleFilterReservation(selectedFilter);
            }
        }
    }, [reservationList]);

    const handleFilterReservation = status => {
        setSelectedFilter(status);

        if (status === 'All Status') {
            setFilteredList(reservationList);
        } else {
            // Filter locally first
            const filtered = reservationList?.filter(
                reservation => reservation.status === status
            );
            setFilteredList(filtered || []);

            // Then fetch from API
            filterReservationHistory(status)
                .then(res => {
                    if (res?.data) {
                        setFilteredList(res.data);
                    }
                })
                .catch(err => console.error('Filter error:', err));
        }
    };

    const getBadgeVariant = status => {
        switch (status) {
            case 'Booking Confirmed':
                return 'success';
            case 'Order Completed':
                return 'success light';
            case 'Booking Cancelled':
                return 'warning';
            case 'Absent':
                return 'dark text-white';
            default:
                return 'secondary';
        }
    };

    const displayList =
        selectedFilter === 'All Status'
            ? filteredList.length > 0
                ? filteredList
                : reservationList
            : filteredList;

    return (
        <>
            {showToast && (
                <div 
                    className="alert alert-danger alert-dismissible fade show" 
                    style={{
                        position: 'fixed',
                        top: 20,
                        right: 20,
                        zIndex: 9999,
                        minWidth: '300px',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                    }}
                >
                    <strong>Error!</strong> {toastMessage}
                    <button 
                        type="button" 
                        className="close" 
                        onClick={() => setShowToast(false)}
                    >
                        <span>&times;</span>
                    </button>
                </div>
            )}
            <div className='d-sm-flex mb-lg-4 mb-2'>
                <div className='mr-auto'>
                    <Form inline>
                        <Row>
                            <Col xs="auto">
                                <Form.Group className="mb-2">
                                    <Form.Label className="mr-2">From:</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={fromDate} 
                                        onChange={(e) => setFromDate(e.target.value)} 
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs="auto">
                                <Form.Group className="mb-2">
                                    <Form.Label className="mr-2">To:</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={toDate} 
                                        onChange={(e) => setToDate(e.target.value)} 
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs="auto">
                                <div>
                                    <Button 
                                        variant="primary" 
                                        className="mb-2 mr-2"
                                        onClick={() => {
                                            // Validate date range
                                            if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
                                                setToastMessage('From date cannot be later than To date');
                                                setShowToast(true);
                                                return;
                                            }
                                            
                                            if (onDateFilter) {
                                                onDateFilter(fromDate, toDate);
                                            }
                                        }}
                                    >
                                        Filter
                                    </Button>
                                    <Button 
                                        variant="outline-secondary" 
                                        className="mb-2"
                                        onClick={() => {
                                            setFromDate('');
                                            setToDate('');
                                            if (onDateFilter) {
                                                onDateFilter('', '');
                                            }
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Dropdown className='dropdown mb-2 ml-auto mr-3'>
                    <Dropdown.Toggle className='i-false btn btn-primary btn-rounded light'>
                        <i className='las la-bolt scale5 mr-3' />
                        {selectedFilter}
                        <i className='las la-angle-down ml-3' />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#'
                            onClick={() =>
                                handleFilterReservation('All Status')
                            }
                        >
                            <span className='text-primary'>All Status</span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#'
                            onClick={() =>
                                handleFilterReservation('Booking Confirmed')
                            }
                        >
                            <span className='text-success'>
                                Booking Confirmed
                            </span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#'
                            onClick={() =>
                                handleFilterReservation('Order Completed')
                            }
                        >
                            <span className='text-primary'>
                                Order Completed
                            </span>
                        </Dropdown.Item>

                        <Dropdown.Item
                            className='dropdown-item'
                            to='#'
                            onClick={() =>
                                handleFilterReservation('Booking Cancelled')
                            }
                        >
                            <span className='text-danger'>
                                Booking Cancelled
                            </span>
                        </Dropdown.Item>
                        <Dropdown.Item
                            className='dropdown-item'
                            to='#'
                            onClick={() => handleFilterReservation('Absent')}
                        >
                            <span className='text-dark'>Absent</span>
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <Card>
                <Card.Header>
                    <Card.Title>Reservation Order History</Card.Title>
                </Card.Header>
                <Card.Body>
                    <Table>
                        <thead>
                            <tr>
                                <th>
                                    <strong>RES. NO.</strong>
                                </th>
                                <th>
                                    <strong>DATE</strong>
                                </th>
                                <th>
                                    <strong>CUSTOMER</strong>
                                </th>
                                <th>
                                    <strong>TABLE NO.</strong>
                                </th>
                                <th>
                                    <strong>NO. OF GUESTS</strong>
                                </th>
                                <th>
                                    <strong>AMOUNT</strong>
                                </th>
                                <th>
                                    <strong>STATUS</strong>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayList ? (
                                displayList.length > 0 ? (
                                    displayList.map(each => (
                                        <tr key={each.bookid}>
                                            <td>
                                                <strong>{each.bookid}</strong>
                                            </td>
                                            <td>
                                                {each.date}
                                            </td>
                                            <td>{each.initiator}</td>
                                            <td>{each.table_no}</td>
                                            <td>{each.n_o_g}</td>
                                            <td>SAR {each.total}</td>
                                            <td>
                                                <Badge
                                                    variant={getBadgeVariant(
                                                        each.status
                                                    )}
                                                >
                                                    {each.status}
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td
                                            colSpan='7'
                                            className='text-center py-4'
                                        >
                                            <p className='text-muted'>
                                                {(fromDate || toDate) ? 
                                                    `No reservations found for the selected date range ${fromDate ? `from ${fromDate}` : ''} ${toDate ? `to ${toDate}` : ''}.` : 
                                                    `No reservations found for ${selectedFilter !== 'All Status' ? selectedFilter : 'any'} status.`
                                                }
                                            </p>
                                        </td>
                                    </tr>
                                )
                            ) : (
                                <tr>
                                    <td colSpan='7'>
                                        <div id='preloader-inner'>
                                            <div className='sk-three-bounce-inner'>
                                                <div className='sk-child sk-bounce1'></div>
                                                <div className='sk-child sk-bounce2'></div>
                                                <div className='sk-child sk-bounce3'></div>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
                <Card.Footer>
                    <div className="d-flex justify-content-between align-items-center">
                        
                        
                        {pagination && pagination.totalPages > 0 && (
                            <Pagination>
                                <Pagination.First 
                                    disabled={pagination.currentPage === 1}
                                    onClick={() => onPageChange && onPageChange(1)}
                                />
                                <Pagination.Prev 
                                    disabled={pagination.currentPage === 1}
                                    onClick={() => onPageChange && onPageChange(pagination.currentPage - 1)}
                                />
                                
                                {/* Show page numbers with ellipsis for many pages */}
                                {[...Array(pagination.totalPages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    // Always show first, last, current, and pages around current
                                    if (
                                        pageNum === 1 ||
                                        pageNum === pagination.totalPages ||
                                        (pageNum >= pagination.currentPage - 1 && pageNum <= pagination.currentPage + 1)
                                    ) {
                                        return (
                                            <Pagination.Item 
                                                key={pageNum} 
                                                active={pageNum === pagination.currentPage}
                                                onClick={() => onPageChange && onPageChange(pageNum)}
                                            >
                                                {pageNum}
                                            </Pagination.Item>
                                        );
                                    }
                                    
                                    // Show ellipsis for gaps
                                    if (
                                        (pageNum === 2 && pagination.currentPage > 3) ||
                                        (pageNum === pagination.totalPages - 1 && pagination.currentPage < pagination.totalPages - 2)
                                    ) {
                                        return <Pagination.Ellipsis key={`ellipsis-${pageNum}`} />;
                                    }
                                    
                                    return null;
                                })}
                                
                                <Pagination.Next 
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    onClick={() => onPageChange && onPageChange(pagination.currentPage + 1)}
                                />
                                <Pagination.Last 
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    onClick={() => onPageChange && onPageChange(pagination.totalPages)}
                                />
                            </Pagination>
                        )}
<div className="d-flex align-items-center">
                            <span className="mr-2">Items per page:</span>
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-primary" size="sm">
                                    {currentPageItems}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => {
                                        setCurrentPageItems(5);
                                        onPageItemsChange && onPageItemsChange(5);
                                    }}>5</Dropdown.Item>
                                    <Dropdown.Item onClick={() => {
                                        setCurrentPageItems(10);
                                        onPageItemsChange && onPageItemsChange(10);
                                    }}>10</Dropdown.Item>
                                    <Dropdown.Item onClick={() => {
                                        setCurrentPageItems(20);
                                        onPageItemsChange && onPageItemsChange(20);
                                    }}>20</Dropdown.Item>
                                    <Dropdown.Item onClick={() => {
                                        setCurrentPageItems(50);
                                        onPageItemsChange && onPageItemsChange(50);
                                    }}>50</Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        
                    </div>
                </Card.Footer>
            </Card>
        </>
    );
};

export default ReservationList;
